import {
  LoginRequestDTO,
  LoginResponseDTO,
  UpdateZohoTokenRequestDTO,
  UpdateZohoTokenResponseDTO,
} from "../models/AuthDTOs";
import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

/**
 * Auth API Service
 * Handles all HTTP requests for authentication
 */

/**
 * HTTP Status Codes
 */
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

/**
 * API Error Class
 */
export class ApiError extends Error {
  constructor(message: string, public status: number, public data?: any) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Create request headers
 */
const createHeaders = (includeAuth = true): Record<string, string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
};

/**
 * Make API request
 */
const makeRequest = async <T>(
  endpoint: string,
  options: RequestInit & { includeAuth?: boolean } = {}
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  const { includeAuth = true, ...fetchOptions } = options;

  const config: RequestInit = {
    headers: createHeaders(includeAuth),
    ...fetchOptions,
  };

  try {
    console.log(`Making API request to: ${url}`, config);

    const response = await fetch(url, config);
    const data = await response.json();

    console.log(`API Response for ${endpoint}:`, data);

    if (!response.ok) {
      throw new ApiError(
        data.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    console.error(`API request failed for ${endpoint}:`, error);
    const err = error as Error;
    throw new ApiError(err.message || "Network error occurred", 0, {
      originalError: error,
    });
  }
};

/**
 * Login API call
 */
export const loginApi = async (
  credentials: LoginRequestDTO
): Promise<LoginResponseDTO> => {
  return makeRequest<LoginResponseDTO>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials),
    includeAuth: false,
  });
};

/**
 * Admin login API call
 * Uses the same endpoint as regular login but validates admin role
 */
export const adminLoginApi = async (
  credentials: LoginRequestDTO
): Promise<LoginResponseDTO> => {
  const response = await makeRequest<LoginResponseDTO>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials),
    includeAuth: false,
  });

  // Check if user is admin (case-insensitive check since backend returns "Admin" capitalized)
  const userRole = response.data.user.role?.toLowerCase();
  if (response.success && userRole !== "admin") {
    throw new ApiError(
      "Access denied. Admin privileges required.",
      HTTP_STATUS.FORBIDDEN
    );
  }

  return response;
};

/**
 * Logout API call
 */
export const logoutApi = async (): Promise<void> => {
  try {
    await makeRequest("/api/auth/logout", {
      method: "POST",
    });
  } catch (error) {
    // Don't throw error on logout - we'll clear local storage anyway
    console.warn("Logout API call failed:", error);
  }
};

/**
 * Update user Zoho token API call
 */
export const updateZohoTokenApi = async (
  refreshToken: string
): Promise<UpdateZohoTokenResponseDTO> => {
  const payload: UpdateZohoTokenRequestDTO = {
    zoho_refresh_token: refreshToken,
  };

  return makeRequest<UpdateZohoTokenResponseDTO>("/api/auth/updateUser", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

/**
 * Get Zoho configuration from backend
 */
export const getZohoConfigApi = async (): Promise<{
  success: boolean;
  data: {
    clientId: string;
    redirectUri: string;
    authUrl: string;
    tokenUrl: string;
    apiUrl: string;
    scope: string;
    responseType: string;
    accessType: string;
    prompt: string;
  };
}> => {
  return makeRequest<{
    success: boolean;
    data: {
      clientId: string;
      redirectUri: string;
      authUrl: string;
      tokenUrl: string;
      apiUrl: string;
      scope: string;
      responseType: string;
      accessType: string;
      prompt: string;
    };
  }>("/api/auth/zohoConfig", {
    method: "GET",
    includeAuth: true,
  });
};

export const getCompanyProfileApi = async (): Promise<{
  success: boolean;
  data: {
    company: import("../models/AuthDTOs").CompanyDTO;
  };
}> => {
  return makeRequest<{
    success: boolean;
    data: {
      company: import("../models/AuthDTOs").CompanyDTO;
    };
  }>("/api/auth/company-profile", {
    method: "GET",
    includeAuth: true,
  });
};

/**
 * Exchange Zoho authorization code for tokens
 */
export const exchangeZohoCodeApi = async (
  code: string
): Promise<{ access_token: string; refreshToken: string }> => {
  const url = `${API_BASE_URL}/api/auth/zohoCallback`;
  const token = getToken();

  if (!token) {
    throw new ApiError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new ApiError(
      errorData.error || `HTTP error! status: ${response.status}`,
      response.status
    );
  }

  return response.json();
};
